import { Formik } from 'formik';
import React, { useEffect, useRef } from 'react';
import { Dashboard } from '@components/common/Dashboard';
import { PageLayout } from '@components/common/PageLayout';
import { DashboardBackButton } from '@components/utils';
import { Banner, BaseContent, ButtonClean, HeadContent, Subsection, Tag, useModalContext, } from '@fourthwall/components';
import { routing } from '@utils/routing';
import { mapStatusToTagProps } from '../../utils/mapStatusToTagProps';
import { ConfirmStoreDisconnectModal } from './components/ConfirmStoreDisconnectModal/ConfirmStoreDisconnectModal';
import { SettingsForm } from './components/SettingsForm/SettingsForm';
import { useShipStation } from './hooks/useShipStation';
export const AppsShipStation = () => {
    const { shipStation } = useShipStation();
    const { open } = useModalContext();
    const formRef = useRef(null);
    useEffect(() => {
        if (!shipStation.settings.stores.length) {
            formRef.current?.setFieldValue('storeId', '');
            return;
        }
        const storeId = shipStation.settings.initialValues.storeId;
        const updatedStoreId = storeId && shipStation.settings.stores?.some((store) => store.value === storeId)
            ? storeId.toString()
            : '';
        formRef.current?.setFieldValue('storeId', updatedStoreId);
    }, [shipStation.settings.stores]);
    const handleDisconnect = () => {
        open(ConfirmStoreDisconnectModal, {
            type: 'DISCONNECT',
            pendingFulfillmentsCount: shipStation.pendingFulfillmentsCount,
            onConfirm: () => {
                formRef.current?.resetForm({
                    values: {
                        apiSecret: '',
                        apiKey: '',
                        storeId: '',
                    },
                });
                shipStation.onDisconnect();
            },
        });
    };
    const renderStatusTag = () => {
        const props = mapStatusToTagProps(shipStation.status);
        if (!props)
            return null;
        return (React.createElement("div", { style: { marginBottom: 20 } },
            React.createElement(Tag, { ...props, size: "large", display: "inline-flex" })));
    };
    const getSettingsErrorBanner = () => {
        if (shipStation.settings.storesErrorCode === 'SHIPSTATION_INCORRECT_CREDENTIALS_ERROR') {
            return (React.createElement(Banner, { appearance: "danger" }, "Please verify your ShipStation credentials and try again."));
        }
        if (shipStation.settings.storesErrorCode === 'SHIPSTATION_PAYMENT_REQUIRED_ERROR') {
            return (React.createElement(Banner, { appearance: "danger" },
                "Please verify your",
                ' ',
                React.createElement("a", { href: routing.external.shipStation.paymentSettings, target: "_blank", rel: "noreferrer" }, "ShipStation license payment"),
                ' ',
                "and try again."));
        }
        if (shipStation.settings.storesErrorCode === 'SHIPSTATION_INCORRECT_CONFIGURATION_ERROR') {
            return (React.createElement(Banner, { appearance: "danger" }, "Please verify your ShipStation configuration and try again."));
        }
        if (shipStation.settings.storesErrorCode === 'SHIPSTATION_INCORRECT_SETTINGS_ERROR') {
            return (React.createElement(Banner, { appearance: "danger" }, "Please verify your ShipStation settings and try again."));
        }
    };
    const renderSettingsForm = () => {
        if (shipStation.isLoading) {
            return null;
        }
        const errorBanner = getSettingsErrorBanner();
        return (React.createElement(Subsection, { title: "Connection" },
            React.createElement(Formik, { innerRef: formRef, initialValues: shipStation.settings.initialValues, onSubmit: shipStation.settings.onSubmit },
                React.createElement(React.Fragment, null,
                    React.createElement("div", { style: { marginBottom: '16px' } },
                        "Connect your ShipStation account using your API key and secret from your",
                        ' ',
                        React.createElement(ButtonClean, { label: "ShipStation API keys page", target: "_blank", href: routing.external.shipStation.apiSettings }),
                        ' ',
                        "in your ShipStation account"),
                    errorBanner && React.createElement("div", { style: { marginBottom: '16px' } }, errorBanner),
                    React.createElement(SettingsForm, { storesOptions: shipStation.settings.stores, storesWarning: shipStation.status === 'NOT_CONFIGURED'
                            ? 'Please select your store to finish configuration'
                            : undefined, isLoading: shipStation.settings.isLoading, isSubmitting: shipStation.settings.isSubmitting, onFetchStores: shipStation.settings.onFetchStores }),
                    React.createElement("div", { style: { marginTop: 16 } },
                        React.createElement(BaseContent, null,
                            React.createElement("ul", null,
                                React.createElement("li", null, "Orders cancelled on ShipStation are automatically cancelled on Fourthwall as well."),
                                React.createElement("li", null, "Orders edited on ShipStation (quantity change, product removal, etc.) will NOT automatically be edited on Fourthwall"),
                                React.createElement("li", null, "It takes up to 24 hours for new orders to be synced. Cancellations are synced instantly."),
                                React.createElement("li", null,
                                    "You can disconnect at any time - your orders and data will remain safe",
                                    (shipStation.status === 'CONNECTED' || shipStation.status === 'BROKEN') && (React.createElement(React.Fragment, null,
                                        ": ",
                                        React.createElement(ButtonClean, { label: "Disconnect", onClick: handleDisconnect })))))))))));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(HeadContent, { title: "ShipStation" }),
        React.createElement(PageLayout, { size: "tiny" },
            React.createElement(Dashboard.Section, { name: "ShipStation", topContent: React.createElement(DashboardBackButton, { label: "Apps", to: routing.apps.self }) },
                React.createElement(Subsection, null,
                    renderStatusTag(),
                    React.createElement("div", null,
                        "Use ShipStation to manage the shipping of products you\u2019re responsible for fulfilling.",
                        ' ',
                        React.createElement(ButtonClean, { label: "Learn more", href: routing.external.helpCenter.shipStationIntegration, target: "_blank" }))),
                renderSettingsForm()))));
};
